// @ts-check
// Configurações > Agendamentos (contrato 10 de agendamento, seção 14): as páginas de agendamento da escola, com tipo,
// duração, anfitriões, próximas reservas e situação, a criação de uma página nova (nome e tipo) e, para cada membro, o
// aceite de ser anfitrião pelas próprias contas Google (revisão de segurança da 3.0.0, B1).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { BOOKING_EVENT_TYPES } from '../../../../shared/booking/booking-values.js';
import { createBookingPage, listBookingHostConsents, listBookingHosts, listBookingPages, setBookingHostConsent } from '../../data/booking-pages.js';
import { startAccountConnection } from '../../data/calendar.js';
import { hostAction, hostConsentState, hostIssue } from '../../lib/booking-pages.js';
import { currentWorkspaceMemberState, hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { Link, navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import { runHostAction } from './host-resolution.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from '../settings/settings-pages.js';
import { AdminOnlyNote, SelectField, StatusBadge, TextField } from '../settings/integration-ui.js';
import { Button } from '../../ui/button.js';
import { Skeleton, Toggle } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/booking-pages.js').BookingPageSummary} BookingPageSummary
 * @typedef {import('../../data/booking-pages.js').BookingHostConsent} BookingHostConsent
 * @typedef {import('../../data/booking-pages.js').BookingHost} BookingHost
 */

const HOST_CONSENT_STATE_KEYS = Object.freeze({
  NOT_ACCEPTED: 'bookingPages.hostConsent.notAccepted',
  EVENT_SCOPE_REQUIRED: 'bookingPages.hostConsent.eventScopeRequired',
  READY: 'bookingPages.hostConsent.accepted',
});

/**
 * Pede ao Google o escopo `calendar.events` para a conta, pela autorização incremental (contrato 10 de agendamento,
 * seção 7.1); o retorno volta para esta tela. Devolve true quando ficou na mesma página (modo demonstração).
 * @param {BookingHostConsent} account
 */
async function requestEventScope(account) {
  const authorizationUrl = await startAccountConnection('google', { calendarEvents: true, connectedAccountId: account.connectedAccountId, returnTo: 'BOOKING_PAGES' });
  // o modo demonstração devolve um caminho local (sem recarregar a página); o real, a tela de consentimento do Google
  if (authorizationUrl.startsWith('/')) {
    navigate(authorizationUrl);
    return true;
  }
  window.location.assign(authorizationUrl);
  return false;
}

/**
 * Aceite de ser anfitrião pelas próprias contas Google: sem ele, a conta não recebe reservas nem mostra as agendas. Aceitar
 * numa conta sem o escopo de criar eventos leva direto ao Google para permitir; a conta só recebe com os dois.
 */
function HostConsentSection() {
  const [accounts, setAccounts] = useState(/** @type {BookingHostConsent[] | null} */ (null));
  const [busyAccountId, setBusyAccountId] = useState(/** @type {string | null} */ (null));
  useEffect(() => {
    listBookingHostConsents().then(setAccounts).catch(() => setAccounts([]));
  }, []);
  /** @param {BookingHostConsent} account @param {boolean} accepted */
  const change = async (account, accepted) => {
    setBusyAccountId(account.connectedAccountId);
    try {
      const updated = await setBookingHostConsent(account.connectedAccountId, accepted);
      setAccounts(updated);
      const current = updated.find((candidate) => candidate.connectedAccountId === account.connectedAccountId);
      if (accepted && current && hostConsentState(current) === 'EVENT_SCOPE_REQUIRED') {
        if (await requestEventScope(current)) setAccounts(await listBookingHostConsents());
        return;
      }
      showSuccessSnackbar(t(accepted ? 'bookingPages.hostConsent.acceptedSnackbar' : 'bookingPages.hostConsent.revokedSnackbar'));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setBusyAccountId(null);
    }
  };
  /** @param {BookingHostConsent} account */
  const allowEvents = async (account) => {
    setBusyAccountId(account.connectedAccountId);
    try {
      if (await requestEventScope(account)) setAccounts(await listBookingHostConsents());
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setBusyAccountId(null);
    }
  };
  return html`<${SettingsSection} title=${t('bookingPages.hostConsent.title')} description=${t('bookingPages.hostConsent.description')}>
    ${accounts === null
      ? html`<${Skeleton} height=${48} radius=${8} />`
      : accounts.length === 0
        ? html`<div class="integration-empty">${t('bookingPages.hostConsent.empty')}</div>`
        : html`<ul class="admin-list" data-testid="booking-host-consent">${accounts.map((account) => html`<li key=${account.connectedAccountId}>
            <div class="channel-card-titles">
              <span class="channel-card-title">${account.handle}</span>
              <span class="settings-table-muted">${t(HOST_CONSENT_STATE_KEYS[hostConsentState(account)])}</span>
            </div>
            ${hostConsentState(account) === 'EVENT_SCOPE_REQUIRED'
              ? html`<${Button} title=${t('bookingPages.hostConsent.allowEvents')} icon="IconRefresh" accent="blue" size="small"
                  disabled=${busyAccountId === account.connectedAccountId} onClick=${() => allowEvents(account)} />`
              : null}
            <${Toggle} value=${Boolean(account.acceptedAt)} disabled=${busyAccountId === account.connectedAccountId}
              ariaLabel=${t('bookingPages.hostConsent.toggleLabel', { handle: account.handle })}
              onChange=${(/** @type {boolean} */ accepted) => change(account, accepted)} />
          </li>`)}</ul>`}
  <//>`;
}

/**
 * Quem pode receber reservas hoje (3.5.0): a equipe inteira com a situação da conta Google de cada um e o botão que
 * resolve a pendência (aceitar e permitir para si; convite para os outros). Só para quem configura as páginas.
 * @param {{ refreshKey: number }} props
 */
function HostReadinessSection({ refreshKey }) {
  const [hosts, setHosts] = useState(/** @type {BookingHost[] | null} */ (null));
  const [busyMemberId, setBusyMemberId] = useState(/** @type {string | null} */ (null));
  const currentMemberId = currentWorkspaceMemberState.value?.id ?? null;
  useEffect(() => {
    listBookingHosts().then(setHosts).catch(() => setHosts([]));
  }, [refreshKey]);
  /** @param {BookingHost} host @param {'ACCEPT' | 'CONNECT' | 'INVITE'} action */
  const resolve = async (host, action) => {
    setBusyMemberId(host.workspaceMemberId);
    try {
      const result = await runHostAction(host, action, { returnTo: 'BOOKING_PAGES' });
      if (result.outcome === 'INVITED' || result.outcome === 'ALREADY_INVITED') {
        showSuccessSnackbar(t(result.outcome === 'INVITED' ? 'bookingPages.hostActions.invited' : 'bookingPages.hostActions.alreadyInvited', { name: result.memberName }));
      } else if (result.outcome === 'READY') {
        setHosts(await listBookingHosts());
        showSuccessSnackbar(t('bookingPages.hostActions.readySnackbar'));
      }
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setBusyMemberId(null);
    }
  };
  const readyCount = (hosts ?? []).filter((host) => hostIssue(host) === null).length;
  return html`<${SettingsSection} title=${t('bookingPages.readiness.title')} description=${t('bookingPages.readiness.description')}>
    ${hosts === null
      ? html`<${Skeleton} height=${64} radius=${8} />`
      : html`<div class="booking-readiness" data-testid="booking-host-readiness">
          <p class="booking-readiness-summary" data-tone=${readyCount > 0 ? 'success' : 'warning'}>
            <${Icon} name=${readyCount > 0 ? 'IconCircleCheck' : 'IconAlertTriangle'} size=${16} />
            ${t(readyCount > 0 ? 'bookingPages.readiness.readyCount' : 'bookingPages.readiness.nobody', { count: readyCount })}
          </p>
          <ul class="booking-host-list">${hosts.map((host) => {
            const issue = hostIssue(host);
            const isSelf = currentMemberId !== null && host.workspaceMemberId === currentMemberId;
            const action = hostAction(issue, isSelf);
            const ready = host.accounts.find((account) => account.isEligible);
            return html`<li key=${host.workspaceMemberId} class="booking-host" data-selected=${issue ? 'false' : 'true'}>
              <div class="booking-host-header">
                <div class="booking-host-titles">
                  <span class="booking-host-name">${host.name || host.email}${isSelf ? html`<span class="booking-host-self">${t('bookingPages.hosts.you')}</span>` : null}</span>
                  <span class="booking-host-status" data-tone=${issue ? 'warning' : 'success'}>
                    <${Icon} name=${issue ? 'IconAlertTriangle' : 'IconBrandGoogle'} size=${14} />
                    ${issue ? t(`bookingPages.hostIssues.${issue}`) : t('bookingPages.hosts.googleReady', { handle: ready?.handle ?? '' })}
                  </span>
                </div>
                ${action === 'RECONNECT'
                  ? html`<${Link} href="/settings/accounts" className="booking-host-link">${t('bookingPages.hosts.openAccounts')}<//>`
                  : action
                    ? html`<${Button} title=${t(`bookingPages.hostActions.${action}`)} icon=${action === 'INVITE' ? 'IconMail' : 'IconBrandGoogle'} accent="blue" size="small"
                        isLoading=${busyMemberId === host.workspaceMemberId} disabled=${busyMemberId !== null} onClick=${() => resolve(host, action)} />`
                    : null}
              </div>
            </li>`;
          })}</ul>
        </div>`}
  <//>`;
}

export function SettingsBookingPagesPage() {
  const [pages, setPages] = useState(/** @type {BookingPageSummary[] | null} */ (null));
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState('');
  const [eventType, setEventType] = useState('VISIT');
  const [isSaving, setIsSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const canEdit = hasCurrentPermissionFlag('WORKSPACE');
  const connectedParam = searchParamsState.value.get('connected');
  const hostParam = searchParamsState.value.get('host');
  const connectionError = searchParamsState.value.get('error');
  useEffect(() => {
    document.title = `${t('bookingPages.title')} - ${t('navigation.settings')}`;
    listBookingPages().then(setPages).catch((error) => {
      showErrorSnackbar(error);
      setPages([]);
    });
  }, []);
  // volta do Google (aceite com a permissão de criar eventos) e o link do convite (?host=1 leva à seção "Ser anfitrião")
  useEffect(() => {
    if (connectedParam) showSuccessSnackbar(t('bookingPages.hostActions.readySnackbar'));
    if (connectionError) showErrorSnackbar(new Error(t(`calendar.accounts.errors.${connectionError === 'ACCESS_DENIED' ? 'ACCESS_DENIED' : 'OAUTH_FAILED'}`)));
    if (connectedParam || connectionError) {
      updateSearchParams({ connected: null, error: null, provider: null, host: null });
      setRefreshKey((value) => value + 1);
    } else if (hostParam) {
      updateSearchParams({ host: null });
      window.setTimeout(() => document.querySelector('[data-testid="booking-host-consent"]')?.scrollIntoView({ block: 'center' }), 50);
    }
  }, [connectedParam, connectionError, hostParam]);

  const create = async () => {
    setIsSaving(true);
    try {
      const page = await createBookingPage({ name: name.trim(), eventType });
      navigate(`/settings/booking-pages/${page.id}`);
    } catch (error) {
      showErrorSnackbar(error);
      setIsSaving(false);
    }
  };

  return html`<${PageCard} header=${html`<${PageHeader} icon="IconCalendarPlus" title=${t('bookingPages.title')} />`}>
    <div class="settings-page booking-settings" data-testid="settings-booking-pages">
      <${SettingsSection} title=${t('bookingPages.title')} description=${t('bookingPages.description')}>
        <ul class="channel-card-explanation">
          <li><${Icon} name="IconBrandGoogle" size=${16} /><span>${t('bookingPages.explanation.google')}</span></li>
          <li><${Icon} name="IconUsers" size=${16} /><span>${t('bookingPages.explanation.crm')}</span></li>
          <li><${Icon} name="IconWorld" size=${16} /><span>${t('bookingPages.explanation.site')}</span></li>
        </ul>
        ${canEdit
          ? html`<div class="integration-toolbar"><${Button} title=${t('bookingPages.new')} icon="IconPlus" accent="blue" size="small" onClick=${() => setIsCreating(true)} /></div>`
          : html`<${AdminOnlyNote} />`}
        ${pages === null
          ? html`<${Skeleton} height=${96} radius=${8} />`
          : pages.length === 0
            ? html`<div class="integration-empty">${t('bookingPages.empty')}</div>`
            : html`<div class="settings-table integration-table" role="table" aria-label=${t('bookingPages.title')}>
                <div class="settings-table-row settings-table-header" data-columns="booking-pages" role="row">
                  <span role="columnheader">${t('bookingPages.columns.name')}</span><span role="columnheader">${t('bookingPages.columns.type')}</span>
                  <span role="columnheader">${t('bookingPages.columns.duration')}</span><span role="columnheader">${t('bookingPages.columns.hosts')}</span>
                  <span role="columnheader">${t('bookingPages.columns.upcoming')}</span><span role="columnheader">${t('bookingPages.columns.status')}</span><span></span>
                </div>
                ${pages.map((page) => html`<${Link} key=${page.id} href=${`/settings/booking-pages/${page.id}`} className="settings-table-row" data-columns="booking-pages" role="row">
                  <span class="settings-table-name" role="cell"><${Icon} name="IconCalendarPlus" size=${16} />${page.name}</span>
                  <span class="settings-table-muted" role="cell">${t(`calendar.eventTypes.${page.eventType}`)}</span>
                  <span class="settings-table-muted" role="cell">${t('bookingPages.minutes', { count: page.durationMinutes })}</span>
                  <span class="settings-table-muted" role="cell">${page.hostCount}</span>
                  <span class="settings-table-muted" role="cell">${page.upcomingBookingCount}</span>
                  <span role="cell"><${StatusBadge} status=${page.isActive ? 'ACTIVE' : 'PAUSED'} label=${page.isActive ? t('bookingPages.status.active') : t('bookingPages.status.paused')} /></span>
                  <span class="settings-table-chevron"><${Icon} name="IconChevronRight" size=${16} /></span>
                <//>`)}
              </div>`}
      <//>
      ${canEdit ? html`<${HostReadinessSection} refreshKey=${refreshKey} />` : null}
      <${HostConsentSection} key=${refreshKey} />
      ${isCreating ? html`<${Modal} title=${t('bookingPages.newTitle')} subtitle=${t('bookingPages.newSubtitle')} confirmLabel=${t('bookingPages.create')}
        isConfirmDisabled=${!name.trim()} isLoading=${isSaving} onClose=${() => setIsCreating(false)} onConfirm=${create}>
        <div class="integration-modal-body">
          <${TextField} label=${t('bookingPages.editor.name')} value=${name} onInput=${setName} placeholder=${t('bookingPages.editor.namePlaceholder')} isRequired=${true} autoFocus=${true} maxLength=${80} />
          <${SelectField} label=${t('bookingPages.editor.eventType')} value=${eventType} onChange=${setEventType}
            options=${BOOKING_EVENT_TYPES.map((type) => ({ value: type, label: t(`calendar.eventTypes.${type}`) }))} hint=${t('bookingPages.editor.eventTypeHint')} />
        </div>
      <//>` : null}
    </div>
  <//>`;
}
